import { Grid, Text } from '@chakra-ui/react';
import { useCallback, useMemo } from 'react';
import {
  ModalBody,
  ModalContent,
  ModalFooterDefault,
  ModalHeader,
  OpenIcon,
  Paragraph,
  ParagraphSizes,
  ParagraphWeights,
  useSnackbar,
} from 'ui-components';
import { useSubmitSmartProfileInformation } from '~/hooks/api/useSubmitSmartProfileInformation';
import { useAddTalentStore } from '~/screens/AddTalent';
import { PlatformInfoCard } from '~/screens/AddTalent/PlatformInfo';

export const PlatformInfo = () => {
  const snackbar = useSnackbar();

  const {
    goToPreviousStep,
    searchResult,
    platforms,
    goToNextStep,
    smartProfileEnabled,
    setSmartProfileRequestId,
    setPlatforms,
    resetProfileFormState,
  } = useAddTalentStore();

  const submitSmartProfiles = useSubmitSmartProfileInformation();

  // Check the initial length of the smart platforms
  const initialProfiles = useMemo(
    () => [...platforms],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleBack = useCallback(() => {
    if (smartProfileEnabled) {
      setPlatforms([]);
    }
    resetProfileFormState();
    goToPreviousStep();
  }, [
    smartProfileEnabled,
    goToPreviousStep,
    setPlatforms,
    resetProfileFormState,
  ]);

  const handleNext = useCallback(() => {
    if (smartProfileEnabled) {
      const accounts = platforms.map((account) => {
        return {
          platform: account?.platform as string,
          handle: account?.handle as string,
        };
      });

      submitSmartProfiles.mutate(
        { accounts: accounts },
        {
          onSuccess: (response) => {
            setSmartProfileRequestId(response.request_id);
            goToNextStep();
          },
          onError: () => {
            snackbar.error({
              description: 'Something went wrong',
            });
          },
        },
      );

      return;
    }

    goToNextStep();
  }, [
    smartProfileEnabled,
    platforms,
    submitSmartProfiles,
    snackbar,
    setSmartProfileRequestId,
    goToNextStep,
  ]);

  const getPlatformMessage = useCallback(() => {
    const smartProfiles = initialProfiles.some(
      (p) => p.type === 'smart-profile',
    );
    // check if smartProfileEnabled is true and initial smartPlatforms length equals 0
    if (smartProfileEnabled && !smartProfiles) {
      return 'Additional platforms not found';
    }

    return `Found ${platforms?.length} platforms`;
  }, [smartProfileEnabled, initialProfiles, platforms]);

  return (
    <ModalContent>
      <ModalHeader>Platform Data</ModalHeader>
      <ModalBody>
        <Grid gap={6}>
          {searchResult?.pipelineStatus === 'invalid' ? (
            <Paragraph size={ParagraphSizes.LG}>
              <Text fontWeight="medium" display="inline">
                Public data is not available
              </Text>
              , click Next to link this social account to the Talent.
            </Paragraph>
          ) : null}
          {platforms?.map((platform, index) => (
            <PlatformInfoCard
              key={index}
              platform={platform.platform}
              platformData={platform}
            />
          ))}

          {smartProfileEnabled ? (
            <Paragraph
              size="lg"
              fontWeight={ParagraphWeights.Regular}
              __css={{
                textAlign: 'center',
              }}
            >
              {getPlatformMessage()}
            </Paragraph>
          ) : null}
        </Grid>
      </ModalBody>
      <ModalFooterDefault
        primary={{
          ariaLabel: 'next',
          onClick: handleNext,
          title: 'Next',
          size: 'md',
          trackId: 'foam-add-talent-platform-info-next',
        }}
        secondary={{
          ariaLabel: 'back',
          onClick: handleBack,
          title: 'Back',
          size: 'md',
          trackId: 'foam-add-talent-platform-info-back',
        }}
        tertiary={{
          ariaLabel: 'Learn more about data',
          as: 'a',
          component: 'TextLink',
          href: 'https://intercom.help/usefoam/en/articles/9362752-unverified-vs-authenticated-data',
          rightIcon: OpenIcon,
          target: '_blank',
          title: 'Learn more about data',
          trackId: 'foam-add-talent-platform-info-learn-more',
        }}
      />
    </ModalContent>
  );
};
